import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PieChart } from "lucide-react";
import axios, { API, fmt, fmtPct, pctColor, STRATEGY_LABELS } from "./commoditiesLib";
import { CommodityTradeTable } from "./CommodityTradeTable";
import { AccountPnL } from "../../components/AccountPnL";

const StatCard = ({ label, value, valueColor, sub }) => (
  <div className="border border-grid p-4">
    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data">{label}</div>
    <div className="font-heading text-2xl font-semibold mt-2" style={valueColor ? { color: valueColor } : undefined}>
      {value}
    </div>
    {sub && <div className="text-xs text-muted-2 mt-1 font-mono-data">{sub}</div>}
  </div>
);

const RollupTable = ({ title, rows, testid }) => (
  <div className="border border-grid" data-testid={testid}>
    <div className="px-4 py-2.5 border-b border-grid text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
      {title}
    </div>
    {Object.keys(rows || {}).length === 0 ? (
      <div className="px-4 py-4 text-xs text-muted-2 font-mono-data">No closed trades yet.</div>
    ) : (
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-muted-3 uppercase tracking-wider text-[10px] font-mono-data border-b border-grid/60">
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2 text-right">Trades</th>
            <th className="px-3 py-2 text-right">Win %</th>
            <th className="px-3 py-2 text-right">Avg Win</th>
            <th className="px-3 py-2 text-right">Avg Loss</th>
            <th className="px-3 py-2 text-right">Profit Factor</th>
            <th className="px-3 py-2 text-right">Net P&L</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(rows).map(([key, r]) => (
            <tr key={key} className="border-b border-grid/40">
              <td className="px-3 py-2 font-semibold">{STRATEGY_LABELS[key] || key}</td>
              <td className="px-3 py-2 text-right text-muted-2">{r.trades}</td>
              <td className="px-3 py-2 text-right text-muted-2">{fmt(r.win_rate_pct, 0)}%</td>
              <td className="px-3 py-2 text-right" style={{ color: pctColor(r.avg_win_pct) }}>
                {r.avg_win_pct == null ? "—" : fmtPct(r.avg_win_pct)}
              </td>
              <td className="px-3 py-2 text-right" style={{ color: pctColor(r.avg_loss_pct) }}>
                {r.avg_loss_pct == null ? "—" : fmtPct(r.avg_loss_pct)}
              </td>
              <td className="px-3 py-2 text-right text-muted-2">{r.profit_factor == null ? "—" : fmt(r.profit_factor, 2)}</td>
              <td className="px-3 py-2 text-right font-semibold" style={{ color: pctColor(r.net_pnl) }}>
                {fmt(r.net_pnl)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

// 2026-10-10 — chat: "Let's have weekly - monthly reports in Commodities P&L
// page (similar to stocks)". Same cards as the Stocks Reports page, from
// /commodities/pnl/periods (real Kite fills only, IST entry date).
const fmtRs = (v) => (v == null ? "—" : `${v >= 0 ? "+" : "-"}₹${fmt(Math.abs(v), 0)}`);
const sign = (v) => (v > 0 ? "bull" : v < 0 ? "bear" : null);

const PeriodCard = ({ title, rollup, testid }) => {
  const r = rollup || {};
  const rows = [
    ["Trades", r.trades ?? 0],
    ["Wins", r.wins ?? 0, "bull"],
    ["Losses", r.losses ?? 0, "bear"],
    ["Win rate", `${r.win_rate_pct ?? 0}%`],
    ["Lots", r.total_lots ?? 0],
    ["Gross P&L (₹)", fmtRs(r.gross_pnl_rs ?? 0), sign(r.gross_pnl_rs)],
    ["Gross P&L (%)", `${(r.gross_pnl_pct ?? 0) >= 0 ? "+" : ""}${fmt(r.gross_pnl_pct ?? 0, 2)}%`, sign(r.gross_pnl_pct)],
    ["Net P&L (₹)", fmtRs(r.net_pnl_rs ?? 0), sign(r.net_pnl_rs)],
    ["Charges (₹, est.)", `-₹${fmt(Math.abs(r.charges_rs ?? 0), 0)}`],
  ];
  return (
    <div className="border border-grid p-4 space-y-1.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data mb-2">{title}</div>
      {rows.map(([label, value, color]) => (
        <div key={label} className="flex justify-between text-sm font-mono-data">
          <span className="text-muted-2">{label}</span>
          <span style={color ? { color: `var(--${color})` } : {}}>{value}</span>
        </div>
      ))}
      {r.by_setup && Object.keys(r.by_setup).length > 0 && (
        <div className="pt-2 border-t border-grid mt-2">
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data mb-1">By Strategy</div>
          {Object.entries(r.by_setup).map(([k, b]) => (
            <div key={k} className="flex justify-between text-[11px] font-mono-data">
              <span className="text-muted-2">{STRATEGY_LABELS[k] || k}</span>
              <span style={{ color: `var(--${sign(b.pnl_rs_net) || "text-muted"})` }}>{b.trades}t · {fmtRs(b.pnl_rs_net)}</span>
            </div>
          ))}
        </div>
      )}
      {r.top_symbols && r.top_symbols.length > 0 && (
        <div className="pt-2 border-t border-grid mt-2">
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data mb-1">By Underlying</div>
          {r.top_symbols.slice(0, 5).map((x) => (
            <div key={x.symbol} className="flex justify-between text-[11px] font-mono-data">
              <span className="text-muted-2">{x.symbol}</span>
              <span style={{ color: `var(--${sign(x.pnl_rs_net) || "text-muted"})` }}>{x.trades}t · {fmtRs(x.pnl_rs_net)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PeriodReports = () => {
  const [p, setP] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async (range) => {
    try {
      setBusy(true);
      const { data } = await axios.get(`${API}/commodities/pnl/periods`, { params: range || {} });
      setP(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load weekly/monthly P&L");
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!p) return null;
  return (
    <div className="space-y-4" data-testid="commodities-pnl-periods">
      <div>
        <h3 className="font-heading text-lg">Weekly &amp; Monthly</h3>
        <div className="text-[10px] text-muted-3 font-mono-data">
          Week starts {p.week_start_ist} · Month starts {p.month_start_ist} · real Kite fills only, by entry date (IST)
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PeriodCard title="This week · live" rollup={p.weekly} testid="commodities-report-weekly" />
        <PeriodCard title="This month · live" rollup={p.monthly} testid="commodities-report-monthly" />
      </div>
      <div className="border border-grid p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">Custom range · live</div>
        <div className="flex flex-wrap items-end gap-3">
          {[["From", fromDate, setFromDate], ["To", toDate, setToDate]].map(([lbl, v, set]) => (
            <label key={lbl} className="block">
              <div className="text-[10px] uppercase tracking-wider text-muted-3 mb-1 font-mono-data">{lbl}</div>
              <input type="date" value={v} onChange={(e) => set(e.target.value)}
                     className="border-grid surface px-2 py-1.5 text-xs font-mono-data" />
            </label>
          ))}
          <button disabled={busy || !fromDate || !toDate} onClick={() => load({ from_date: fromDate, to_date: toDate })}
                  className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data border border-grid hover:bg-white/5 disabled:opacity-50">
            {busy ? "Loading…" : "Apply"}
          </button>
          {p.custom && (
            <button onClick={() => { setFromDate(""); setToDate(""); load(); }}
                    className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data border border-grid hover:bg-white/5">
              Clear
            </button>
          )}
        </div>
        {p.custom && (
          <div className="max-w-md">
            <PeriodCard title={`Live · ${p.custom_from} → ${p.custom_to}`} rollup={p.custom} testid="commodities-report-custom" />
          </div>
        )}
      </div>
    </div>
  );
};

const CommoditiesPnL = () => {
  const [data, setData] = useState(null);

  // 2026-09-15 — chat: "Commodities -> P&L Page: can we track real market
  // orders (Profit and loss)? similar to Stocks....remove Paper trade
  // completely." /commodities/pnl now returns real_pnl_summary() (Kite
  // fills only — see commodity_autopilot.py), which already includes
  // closed_trades/open_trades in the SAME response, so this page no longer
  // makes a second call to /commodities/paper-trades (that endpoint mixes in
  // every simulated Black-76 trade too, which is exactly what this page used
  // to show and what the chat asked to remove).
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/commodities/pnl`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load P&L");
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) return <div className="text-muted-2 font-mono-data text-xs">Loading…</div>;

  const o = data.overall || {};
  const openTrades = data.open_trades || [];
  const closedTrades = data.closed_trades || [];

  return (
    <section className="space-y-5" data-testid="commodities-pnl-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
          Commodities · MCX
        </div>
        <h2 className="font-heading text-2xl flex items-center gap-2">
          <PieChart size={20} /> P&L
        </h2>
        <div className="text-[10px] text-muted-3 font-mono-data mt-1">
          Real Kite-executed trades only, last {data.retention_days} days · entry/exit are actual broker fills,
          net of estimated MCX charges. Simulated (paper) trades are excluded from this page. A row marked
          &quot;Unconfirmed&quot; closed for real but its exit fill couldn&apos;t be recovered from Kite yet — it's
          shown but excluded from the stats above until confirmed; check your Kite app directly for that one.
        </div>
      </div>

      {/* 2026-10-27 — real Kite fills per account, incl. manual orders (kite_ledger.py) */}
      <AccountPnL market="commodities" title="Commodities · account P&L from real fills (auto + manual)" />
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">Autopilot trades (primary account)</div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Trades" value={o.trades ?? 0} />
        <StatCard label="Win Rate" value={`${fmt(o.win_rate_pct, 0)}%`} />
        <StatCard label="Profit Factor" value={o.profit_factor == null ? "—" : fmt(o.profit_factor, 2)} />
        <StatCard label="Charges (est.)" value={fmt(o.charges_total)} />
        <StatCard label="Net P&L" value={fmt(o.net_pnl)} valueColor={pctColor(o.net_pnl)} />
      </div>

      <PeriodReports />

      <RollupTable title="By Strategy" rows={data.by_strategy} testid="commodities-pnl-by-strategy" />
      <RollupTable title="By Underlying" rows={data.by_underlying} testid="commodities-pnl-by-underlying" />

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data mb-1.5">
          Open Real Positions {openTrades ? `(${openTrades.length})` : ""}
        </div>
        {/* 2026-09-16 — chat: "Remove Exit option from P&L page, that need
            to be in Live Orders page only." No onExit here — see
            CommoditiesExecution.jsx's LiveAttemptRow for the real exit
            action now that it lives on the Live Orders page instead. */}
        <CommodityTradeTable
          trades={openTrades}
          emptyLabel="No open real (Kite) positions."
          testid="commodities-pnl-open-trades"
        />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data mb-1.5">
          Closed Real Trades {closedTrades ? `(${closedTrades.length})` : ""}
        </div>
        <CommodityTradeTable
          trades={closedTrades}
          emptyLabel="No closed real (Kite) trades yet."
          testid="commodities-pnl-closed-trades"
        />
      </div>
    </section>
  );
};

export default CommoditiesPnL;
